/**
 * Detalle de un mensaje.
 *
 * Es la pantalla que tiene que igualar (y superar) al log de la integracion
 * SAP-WMS actual: el JSON que se envio, la respuesta que llego y el reintento.
 * Aca eso son tres cosas visibles a la vez:
 *
 * 1. Payload crudo (la evidencia, inmutable) y payload normalizado (lo que la
 *    plataforma entendio) uno al lado del otro.
 * 2. Historial de intentos, cada uno con su request y su response.
 * 3. El correlation id como enlace a la traza del pedido entre sistemas.
 *
 * Con la actualizacion automatica encendida (switch del encabezado, preferencia
 * `run` compartida con el flujo y la traza) el grafo de la corrida se refresca
 * cada 2 s y el detalle cada 5 s mientras el mensaje no llegue a un estado final.
 */
import { useCallback, useState } from 'react';
import type { ReactElement } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { flowNodeId } from '../api/contract-schemas';
import type { FlowGraphNode, MessageAttempt, MessageLogEntry } from '../api/contracts';
import { useMonitorApi } from '../app/api-context';
import { AutoRefreshToggle } from '../components/AutoRefreshToggle';
import { ErrorBanner } from '../components/ErrorBanner';
import { EmptyState, LoadingBlock } from '../components/Feedback';
import { FlowGraphCanvas, FlowGraphLegend } from '../components/FlowGraphCanvas';
import { FlowStepDetail } from '../components/FlowStepDetail';
import { JsonPanel } from '../components/JsonPanel';
import { KeyValueList } from '../components/KeyValueList';
import {
  ProcessReferencePanel,
  ProcessReferenceToggle,
  useProcessReferencePanel,
} from '../components/ProcessReferencePanel';
import { QuarantineIssues } from '../components/QuarantineIssues';
import { OutcomeBadge, DirectionBadge, StatusBadge } from '../components/StatusBadge';
import { useAutoRefreshPreference } from '../hooks/use-auto-refresh-preference';
import { useLiveFlowGraph } from '../hooks/use-live-flow-graph';
import type { LiveFlowGraph } from '../hooks/use-live-flow-graph';
import { usePolledResource } from '../hooks/use-polled-resource';
import {
  REFRESH_INTERVAL_MS,
  describeInterval,
  isSettledStatus,
  latestUpdate,
} from '../lib/auto-refresh';
import { flowLabel, quarantineReasonLabel, resolutionLabel, systemLabel } from '../lib/catalogs';
import { resolveStepTarget } from '../lib/flow-steps';
import { formatDateTime, formatDuration } from '../lib/format';
import { flowsOfRun } from '../lib/process-reference';

function AttemptCard({ attempt }: { readonly attempt: MessageAttempt }): ReactElement {
  const failed = attempt.outcome !== 'ok';
  // Abierto o cerrado se decide UNA vez, al aparecer: despues manda el usuario.
  // Con `open={failed}` un refresco cerraria un intento en curso (se registra
  // como `error` hasta que termina) justo cuando alguien lo esta leyendo.
  const [initiallyOpen] = useState(failed);
  return (
    <details className={`attempt attempt--${failed ? 'error' : 'ok'}`} open={initiallyOpen}>
      <summary className="attempt__summary">
        <span className="attempt__no">Intento {attempt.attemptNo}</span>
        <OutcomeBadge outcome={attempt.outcome} />
        <span className="muted mono">{formatDateTime(attempt.startedAt)}</span>
        <span className="muted">
          duración {formatDuration(attempt.startedAt, attempt.finishedAt)}
        </span>
      </summary>
      <div className="attempt__body">
        {attempt.error === null ? null : <p className="attempt__error">{attempt.error}</p>}
        <JsonPanel
          title="Request enviado"
          value={attempt.requestSnapshot}
          emptyLabel="Este intento no registró request (falló antes de salir)."
        />
        <JsonPanel
          title="Response recibido"
          value={attempt.responseSnapshot}
          emptyLabel="Sin respuesta registrada."
        />
      </div>
    </details>
  );
}

/**
 * El flujo de la corrida a la que pertenece este evento, con su paso marcado.
 *
 * POR QUE ACA Y NO SOLO EN SU PANTALLA: a un evento se llega por un dead letter,
 * por la tabla o por un enlace, y la primera pregunta es siempre la misma —en
 * que parte del flujo cayo y que venia antes—. Tenerlo que buscar en otra
 * pantalla obliga a reconstruir el contexto de memoria.
 *
 * SE CARGA APARTE DEL MENSAJE, a proposito: si el grafo falla o tarda, el
 * detalle —payloads, intentos, el error— se ve igual. Es informacion de contexto
 * y no puede tapar la evidencia, que es a lo que se vino. La carga la hace la
 * pantalla y llega por `resource`, para que el switch y "Actualizar" del
 * encabezado muevan el grafo junto con el mensaje.
 */
function MessageFlowGraph({
  message,
  resource,
}: {
  readonly message: MessageLogEntry;
  readonly resource: LiveFlowGraph;
}): ReactElement | null {
  const navigate = useNavigate();
  const [selected, setSelected] = useState<FlowGraphNode | null>(null);

  const currentNodeId = flowNodeId({
    direction: message.direction,
    system: message.system,
    eventType: message.eventType,
  });

  const onSelectStep = useCallback(
    (step: FlowGraphNode) => {
      const target = resolveStepTarget(step);
      if (target.kind === 'message' && target.messageId !== message.id) {
        navigate(`/mensajes/${target.messageId}`);
        return;
      }
      setSelected(step);
    },
    [message.id, navigate],
  );

  const graph = resource.data;
  const selectedStep = graph?.nodes.find((step) => step.id === selected?.id) ?? null;
  // La guía del flujo: el mismo panel que en la pantalla del flujo. Primero la del
  // flujo de ESTE evento, que puede no ser el de la corrida (una carga de
  // catálogo es de F12 y tiene pasos de F13); después, la de los demás.
  const reference = useProcessReferencePanel([message.flow, ...flowsOfRun(graph)]);
  // Un hilo de un solo paso no es un flujo: la caja sola no dice nada que el
  // encabezado del evento no diga ya.
  if (graph === null || graph.nodes.length < 2) {
    return null;
  }

  return (
    <>
      <section className="panel">
        <header className="panel__header">
          <h2>Flujo de la corrida</h2>
          <FlowGraphLegend />
        </header>
        <div className="panel__body">
          <ErrorBanner error={resource.error} onRetry={resource.reload} />
          <FlowGraphCanvas
            graph={graph}
            currentNodeId={currentNodeId}
            selectedNodeId={selected?.id ?? null}
            onSelectStep={onSelectStep}
            height={520}
          />
          <p className="muted">
            Pincha un paso para abrir su evento.{' '}
            <Link to={`/flujo/${encodeURIComponent(message.correlationId)}`}>
              Ver el flujo a pantalla completa
            </Link>
            {' · '}
            <Link to={`/traza/${encodeURIComponent(message.correlationId)}`}>
              ver la traza completa
            </Link>
            {reference.primary === null ? null : (
              <>
                {' · '}
                <ProcessReferenceToggle state={reference} variant="link">
                  Referencia de {reference.primary.id}
                </ProcessReferenceToggle>
              </>
            )}
          </p>
        </div>
      </section>

      {selectedStep === null ? null : (
        <FlowStepDetail step={selectedStep} onClose={() => setSelected(null)} />
      )}

      <ProcessReferencePanel state={reference} />
    </>
  );
}

/**
 * Que dice el switch del encabezado sobre lo que se esta actualizando: el numero
 * solo ("cada 2 s") no alcanza, porque en esta pantalla se mueven dos cosas.
 */
function refreshHint(messageSettled: boolean): string {
  const graphEvery = describeInterval(REFRESH_INTERVAL_MS.flowGraph);
  const messageEvery = describeInterval(REFRESH_INTERVAL_MS.messageDetail);
  return messageSettled
    ? `El mensaje ya llegó a un estado final y no se vuelve a descargar; el flujo de la corrida se sigue actualizando ${graphEvery}.`
    : `El flujo de la corrida se actualiza ${graphEvery} y el mensaje ${messageEvery}, hasta que llegue a un estado final.`;
}

export function MessageDetailPage(): ReactElement {
  const api = useMonitorApi();
  const { id } = useParams<{ id: string }>();
  const messageId = id ?? '';
  const [autoRefresh, setAutoRefresh] = useAutoRefreshPreference('run');
  // El detalle se vuelve a pedir SOLO mientras el mensaje pueda cambiar solo.
  // Trae el payload crudo, el normalizado y el request/response de cada intento:
  // puede pesar cientos de KB. En un estado final (procesado, dead letter,
  // cuarentena) ya no cambia sin que una persona actue —reprocesar, descartar,
  // revisar—, y para ver ese cambio esta "Actualizar". Bajarlo cada 5 s seria
  // trafico perdido. El grafo, liviano, se sigue refrescando: la corrida puede
  // seguir avanzando despues de este mensaje.
  const resource = usePolledResource(() => api.getMessage(messageId), [api, messageId], {
    intervalMs: REFRESH_INTERVAL_MS.messageDetail,
    enabled: autoRefresh,
    shouldPoll: (detail) => !isSettledStatus(detail.message.status),
  });
  const graph = useLiveFlowGraph(resource.data?.message.correlationId ?? null, {
    enabled: autoRefresh,
  });
  const reloadAll = (): void => {
    resource.reload();
    graph.reload();
  };

  if (resource.loading && resource.data === null) {
    return <LoadingBlock label="Cargando el mensaje…" />;
  }

  // Sin dato no hay nada que mostrar. Con dato, un error (un refresco que fallo)
  // se avisa arriba sin tapar el mensaje: la evidencia no desaparece por un corte.
  if (resource.data === null) {
    return (
      <div className="page">
        <ErrorBanner error={resource.error} onRetry={resource.reload} />
        {resource.error === null ? <EmptyState title="El mensaje no existe." /> : null}
        <p>
          <Link to="/mensajes">← Volver a los mensajes</Link>
        </p>
      </div>
    );
  }

  const { message, attempts, deadLetter, quarantine } = resource.data;

  return (
    <div className="page">
      <header className="page__header">
        <div>
          <p className="muted">
            <Link to="/mensajes">← Mensajes</Link>
          </p>
          <h1>
            {systemLabel(message.system)} · {message.eventType ?? 'sin evento'}
          </h1>
          <p className="page__subtitle mono">{message.id}</p>
        </div>
        <div className="row">
          <StatusBadge status={message.status} />
          <DirectionBadge direction={message.direction} />
          <AutoRefreshToggle
            enabled={autoRefresh}
            onChange={setAutoRefresh}
            intervalMs={REFRESH_INTERVAL_MS.flowGraph}
            updatedAt={latestUpdate(resource.updatedAt, graph.updatedAt)}
            failing={resource.error !== null || graph.error !== null}
            hint={refreshHint(isSettledStatus(message.status))}
          />
          <button type="button" className="button" onClick={reloadAll}>
            Actualizar
          </button>
        </div>
      </header>

      <ErrorBanner error={resource.error} onRetry={resource.reload} />

      {deadLetter === null ? null : (
        <div className="banner banner--error">
          <div className="banner__title">Este mensaje está en dead letter.</div>
          <div>
            {deadLetter.reason} · {resolutionLabel(deadLetter.resolution)} ·{' '}
            <Link to="/dead-letters">ir a dead letters</Link>
          </div>
          {/*
            Esta es la pantalla a la que se llega desde la traza por correlation
            id: el camino del "seis meses despues, ¿que paso con este pedido?".
            Si el cierre y su motivo no se ven aca, hay que ir a buscarlos a otra
            pantalla, que es casi lo mismo que no tenerlos.
          */}
          {deadLetter.resolvedBy === null ? null : (
            <div>
              Cerrada por {deadLetter.resolvedBy} el {formatDateTime(deadLetter.resolvedAt)}
              {deadLetter.resolutionNote === null ? '.' : `: ${deadLetter.resolutionNote}`}
            </div>
          )}
        </div>
      )}

      {quarantine === null ? null : (
        <div className="banner banner--warning">
          <div className="banner__title">
            Payload en cuarentena: {quarantineReasonLabel(quarantine.reason)}
          </div>
          <div>
            Registrado el {formatDateTime(quarantine.createdAt)} ·{' '}
            <Link to="/cuarentena">ir a cuarentena</Link>
          </div>
          {quarantine.resolvedBy === null ? null : (
            <div>
              Revisado por {quarantine.resolvedBy} el {formatDateTime(quarantine.resolvedAt)}
              {quarantine.resolutionNote === null ? '.' : `: ${quarantine.resolutionNote}`}
            </div>
          )}
        </div>
      )}

      <MessageFlowGraph message={message} resource={graph} />

      <section className="panel">
        <header className="panel__header">
          <h2>Identificación</h2>
        </header>
        <div className="panel__body">
          <KeyValueList
            items={[
              {
                label: 'Correlation id',
                value: (
                  <span>
                    <Link
                      className="mono"
                      to={`/traza/${encodeURIComponent(message.correlationId)}`}
                    >
                      {message.correlationId}
                    </Link>{' '}
                    →{' '}
                    <Link to={`/traza/${encodeURIComponent(message.correlationId)}`}>
                      ver traza completa
                    </Link>{' '}
                    ·{' '}
                    <Link to={`/flujo/${encodeURIComponent(message.correlationId)}`}>
                      abrir el flujo
                    </Link>
                  </span>
                ),
              },
              {
                label: 'Clave de idempotencia',
                value: <span className="mono">{message.idempotencyKey ?? '—'}</span>,
              },
              { label: 'Flujo', value: flowLabel(message.flow) },
              { label: 'Sistema', value: systemLabel(message.system) },
              { label: 'Versión de schema', value: message.schemaVersion ?? '—' },
              { label: 'Intentos', value: message.attempts },
              { label: 'HTTP', value: message.httpStatus ?? '—' },
              {
                // Un resultado esperado que no es 2xx (el -4006 de SAP): explica el
                // HTTP de arriba sin ser un error.
                label: 'Nota',
                value: message.note ?? '—',
              },
              { label: 'IP de origen', value: message.sourceIp ?? '—' },
              { label: 'Ocurrió', value: formatDateTime(message.occurredAt) },
              { label: 'Recibido', value: formatDateTime(message.receivedAt) },
              { label: 'Procesado', value: formatDateTime(message.processedAt) },
              {
                label: 'Último error',
                value:
                  message.lastError === null ? (
                    '—'
                  ) : (
                    <span className="mono">{message.lastError}</span>
                  ),
              },
            ]}
          />
        </div>
      </section>

      <div className="grid-2">
        <JsonPanel
          title="Payload crudo"
          value={message.payloadRaw}
          emptyLabel="Sin payload crudo registrado."
          meta={<span className="muted">tal como llegó o se envió</span>}
        />
        <JsonPanel
          title="Payload normalizado"
          value={message.payloadNormalized}
          emptyLabel="Todavía no hay payload normalizado: el mensaje no pasó por la validación."
          meta={<span className="muted">lo que entendió la plataforma</span>}
        />
      </div>

      {message.headers === null ? null : (
        <JsonPanel title="Cabeceras HTTP (redactadas)" value={message.headers} />
      )}

      {quarantine === null ? null : (
        <section className="panel">
          <header className="panel__header">
            <h2>Por qué se rechazó</h2>
          </header>
          <div className="panel__body">
            <QuarantineIssues zodIssues={quarantine.zodIssues} />
          </div>
        </section>
      )}

      <section className="panel">
        <header className="panel__header">
          <h2>Historial de intentos ({attempts.length})</h2>
        </header>
        <div className="panel__body">
          {attempts.length === 0 ? (
            <EmptyState
              title="Sin intentos registrados."
              hint="El mensaje se persistió pero todavía no lo tomó ningún worker."
            />
          ) : (
            attempts.map((attempt) => <AttemptCard key={attempt.id} attempt={attempt} />)
          )}
        </div>
      </section>

      {deadLetter === null ? null : (
        <JsonPanel
          title="Historial congelado en la dead letter"
          value={deadLetter.history}
          emptyLabel="La dead letter no guardó historial."
        />
      )}
    </div>
  );
}
